import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArticleDisclaimer, useViewCatalogueLink } from '@/components/article';
import { NavigationPill } from '@/components/home';
import { PageHeader, PageShell } from '@/components/layout';
import { Button, RevealOnView } from '@/components/ui';
import {
  BUTTON_SLIDE,
  CONTENT_SLIDE,
  HEADER_FADE,
  REVEAL_TIMING,
  useInView,
} from '@/utils/animation';
import { OurStoryRow } from '@/pages/OurStory/components';
import { DownloadButton } from './components';
import {
  FITTING_GUIDE_ITEMS,
  FITTING_GUIDES_SECTION_ID,
  IFU_SECTION_ID,
  INSTRUCTION_DOCS,
  PACKAGE_INSERT_ITEMS,
  PACKAGE_INSERTS_SECTION_ID,
  type ProductDocument,
} from './content';

const BEAT_MS = 200;
const INTRO_DELAY_MS = Math.round(HEADER_FADE.durationMs * 0.5);
const REST_DELAY_MS = INTRO_DELAY_MS + Math.round(REVEAL_TIMING.durationMs * 0.6);
const BUTTON_DELAY_MS = INTRO_DELAY_MS + REVEAL_TIMING.durationMs;

const headerFadeStyle = (visible: boolean) => ({
  opacity: visible ? 1 : 0,
  transition: `opacity ${HEADER_FADE.durationMs}ms ${HEADER_FADE.ease}`,
});

export default function PatientCare() {
  const { t } = useTranslation();
  const viewCatalogueLink = useViewCatalogueLink();
  const introParagraphs = t('patientCare.header.intro', { returnObjects: true }) as string[];

  const [section1Visible, setSection1Visible] = useState(false);
  useEffect(() => {
    const timer = setTimeout(() => setSection1Visible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, []);

  const packageRef = useRef<HTMLElement>(null);
  const packageInView = useInView(packageRef, { threshold: 0 });
  const [packageVisible, setPackageVisible] = useState(false);
  useEffect(() => {
    if (!packageInView) return;
    const timer = setTimeout(() => setPackageVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [packageInView]);

  const fittingRef = useRef<HTMLElement>(null);
  const fittingInView = useInView(fittingRef, { threshold: 0 });
  const [fittingVisible, setFittingVisible] = useState(false);
  useEffect(() => {
    if (!fittingInView) return;
    const timer = setTimeout(() => setFittingVisible(true), BEAT_MS);
    return () => clearTimeout(timer);
  }, [fittingInView]);

  const itemLabel = (item: ProductDocument) => t(`products.items.${item.productKey}.name`);
  const itemSubLabel = (item: ProductDocument) =>
    item.subLabelKey ? t(`patientCare.subLabels.${item.subLabelKey}`) : undefined;

  return (
    <PageShell
      title={t('patientCare.meta.title')}
      description={t('patientCare.meta.description')}
      path="/patient-care"
      hideHeader
    >
      <NavigationPill />
      <PageHeader title={t('patientCare.pageHeader')} links={viewCatalogueLink} />

      <section
        aria-labelledby="patient-care-heading"
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col gap-30">
          <div className="flex w-full flex-col gap-10 md:flex-row md:items-start md:justify-between">
            <h2
              id="patient-care-heading"
              style={headerFadeStyle(section1Visible)}
              className="text-[32px] font-normal leading-8 tracking-[-0.025em] text-clearlab-dusk md:min-w-[240px] md:max-w-[440px] md:flex-1 xl:w-[440px] xl:flex-none"
            >
              {t('patientCare.header.title')}
            </h2>
            <div className="flex flex-col items-start gap-10 md:w-[600px]">
              <RevealOnView
                {...CONTENT_SLIDE}
                inView={section1Visible}
                delayMs={INTRO_DELAY_MS}
                className="flex w-full flex-col"
              >
                {introParagraphs.map((paragraph, idx) => (
                  <p
                    key={idx}
                    className={`w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight ${
                      idx < introParagraphs.length - 1 ? 'mb-10' : ''
                    }`}
                  >
                    {paragraph}
                  </p>
                ))}
              </RevealOnView>
              <RevealOnView {...BUTTON_SLIDE} inView={section1Visible} delayMs={BUTTON_DELAY_MS}>
                <Button
                  to="/contact"
                  variant="outlined"
                  size="small"
                  tone="blue"
                  iconRight
                  className="px-4 py-2"
                >
                  {t('patientCare.header.cta')}
                </Button>
              </RevealOnView>
            </div>
          </div>

          <RevealOnView
            {...CONTENT_SLIDE}
            inView={section1Visible}
            delayMs={REST_DELAY_MS}
            className="flex flex-col gap-30"
          >
            <hr className="w-full border-0 border-t border-clearlab-divider-darker" />

            <OurStoryRow title={t('patientCare.ifu.title')}>
              <div className="flex w-full flex-col items-start gap-20">
                <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                  {t('patientCare.ifu.intro')}
                </p>
                <div className="flex w-full flex-col gap-20">
                  {INSTRUCTION_DOCS.map((doc) => (
                    <div key={doc.href} className="flex w-full flex-col items-start gap-6">
                      <div className="flex w-full items-start gap-6">
                        <p className="shrink-0 text-[16px] font-normal leading-[44px] text-clearlab-steel">
                          {t('patientCare.ifu.title')}
                        </p>
                        <p className="flex-1 text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                          {doc.languages}
                        </p>
                      </div>
                      <DownloadButton href={doc.href} label={t('patientCare.ifu.downloadButton')} />
                    </div>
                  ))}
                </div>
              </div>
            </OurStoryRow>
          </RevealOnView>
        </div>
        <span id={IFU_SECTION_ID} className="sr-only">
          {t('patientCare.ifu.title')}
        </span>
      </section>

      <section
        ref={packageRef}
        aria-labelledby={PACKAGE_INSERTS_SECTION_ID}
        className="bg-clearlab-divider-light/20 px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <OurStoryRow title={t('patientCare.packageInserts.title')} visible={packageVisible}>
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={packageVisible}
              delayMs={INTRO_DELAY_MS}
              className="flex w-full flex-col items-start gap-20"
            >
              <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                {t('patientCare.packageInserts.intro')}
              </p>
              <div className="flex w-full flex-col gap-10">
                {PACKAGE_INSERT_ITEMS.map((item) => {
                  const subLabel = itemSubLabel(item);
                  return (
                    <div key={item.productKey} className="flex w-full items-center gap-4">
                      {subLabel ? (
                        <div className="flex flex-1 flex-col items-start gap-1">
                          <p className="text-[16px] font-normal leading-[18px] text-clearlab-midnight">
                            {itemLabel(item)}
                          </p>
                          <p className="text-[12px] font-semibold leading-[14px] text-clearlab-steel">
                            {subLabel}
                          </p>
                        </div>
                      ) : (
                        <p className="flex-1 text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                          {itemLabel(item)}
                        </p>
                      )}
                      <div className="flex flex-1">
                        <DownloadButton
                          href={item.href}
                          label={t('patientCare.packageInserts.downloadButton')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </RevealOnView>
          </OurStoryRow>
        </div>
        <span id={PACKAGE_INSERTS_SECTION_ID} className="sr-only">
          {t('patientCare.packageInserts.title')}
        </span>
      </section>

      <section
        ref={fittingRef}
        aria-labelledby={FITTING_GUIDES_SECTION_ID}
        className="bg-clearlab-oat px-6 py-30 md:px-14 lg:px-20 lg:py-50 xl:px-30"
      >
        <div className="mx-auto flex w-full max-w-content-xl flex-col">
          <OurStoryRow title={t('patientCare.fittingGuides.title')} visible={fittingVisible}>
            <RevealOnView
              {...CONTENT_SLIDE}
              inView={fittingVisible}
              delayMs={INTRO_DELAY_MS}
              className="flex w-full flex-col items-start gap-20"
            >
              <p className="w-full text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                {t('patientCare.fittingGuides.intro')}
              </p>
              <div className="flex w-full flex-col gap-10">
                {FITTING_GUIDE_ITEMS.map((item) => {
                  const subLabel = itemSubLabel(item);
                  return (
                    <div key={item.productKey} className="flex w-full items-center gap-4">
                      {subLabel ? (
                        <div className="flex flex-1 flex-col items-start gap-1">
                          <p className="text-[16px] font-normal leading-[18px] text-clearlab-midnight">
                            {itemLabel(item)}
                          </p>
                          <p className="text-[12px] font-semibold leading-[14px] text-clearlab-steel">
                            {subLabel}
                          </p>
                        </div>
                      ) : (
                        <p className="flex-1 text-[16px] font-normal leading-[44px] text-clearlab-midnight">
                          {itemLabel(item)}
                        </p>
                      )}
                      <div className="flex flex-1">
                        <DownloadButton
                          href={item.href}
                          label={t('patientCare.fittingGuides.downloadButton')}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </RevealOnView>
          </OurStoryRow>
        </div>
        <span id={FITTING_GUIDES_SECTION_ID} className="sr-only">
          {t('patientCare.fittingGuides.title')}
        </span>
      </section>

      <RevealOnView {...CONTENT_SLIDE} inView={fittingVisible} delayMs={INTRO_DELAY_MS}>
        <ArticleDisclaimer text={t('patientCare.disclaimer')} />
      </RevealOnView>
    </PageShell>
  );
}
